import { Role } from "@cryptors/api-client";
import {
  Box,
  CircularProgress,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import { usePermissions } from "@refinedev/core";
import { List } from "@refinedev/mui";
import { useState } from "react";

import {
  useGetComparisonKpis,
  useGetDashboardCollections,
  useGetNetworkKpis,
  useGetPeriodKpis,
  useGetSnapshotKpis,
} from "@/api/stats.api";
import AdminKpisSection from "@/components/dashboard/AdminKpisSection";
import { AttractivitySection } from "@/components/dashboard/AttractivitySection";
import { ComparisonSection } from "@/components/dashboard/ComparisonSection";
import { EngagementSection } from "@/components/dashboard/EngagementSection";
import { GeneralPlayerStatsSection } from "@/components/dashboard/GeneralPlayerStatsSection";
import { NationalCollectionsTopSection } from "@/components/dashboard/NationalCollectionsTopSection";
import { NetworkSection } from "@/components/dashboard/NetworkSection";
import {
  PeriodFilters,
  PeriodFiltersValue,
} from "@/components/dashboard/PeriodFilters";
import { PlayerProfileSection } from "@/components/dashboard/PlayerProfileSection";
import { VisitsHeatmap } from "@/components/dashboard/VisitsHeatmap";
import { getCurrentUserKey } from "@/providers/authProvider";

import {
  clientDefaultPeriodFilters,
  defaultPeriodFilters,
} from "./dashboard-default-filters";

const DashboardLoading = () => (
  <List title="Tableau de bord">
    <Box display="flex" justifyContent="center" p={4}>
      <CircularProgress />
    </Box>
  </List>
);

const DashboardError = () => (
  <List title="Tableau de bord">
    <Typography color="error">Échec du chargement des indicateurs.</Typography>
  </List>
);

const SectionLoading = ({ label }: { label: string }) => (
  <Box display="flex" alignItems="center" gap={2} p={3}>
    <CircularProgress size={24} />
    <Typography color="text.secondary">{label}</Typography>
  </Box>
);

const SectionError = ({ message }: { message: string }) => (
  <Typography color="error" p={2}>
    {message}
  </Typography>
);

interface DashboardContentProps {
  initialFilters: PeriodFiltersValue;
  isClient: boolean;
}

const DashboardContent = ({
  initialFilters,
  isClient,
}: DashboardContentProps) => {
  const { data, isLoading, isError } = useGetSnapshotKpis(!isClient);
  const [filters, setFilters] = useState(() => initialFilters);
  const [topMode, setTopMode] = useState<"top5" | "all">("top5");
  const networkQuery = useGetNetworkKpis(isClient);
  const periodQuery = useGetPeriodKpis(filters);
  const comparisonQuery = useGetComparisonKpis(!isClient);

  return (
    <List title="Tableau de bord — KPIs clés à date">
      <Stack gap={3}>
        {!isClient && isLoading && (
          <SectionLoading label="Chargement des indicateurs globaux…" />
        )}
        {!isClient && isError && (
          <SectionError message="Échec du chargement des indicateurs globaux." />
        )}
        {data && <AdminKpisSection data={data} />}
        <PeriodFilters
          value={filters}
          onChange={setFilters}
          topMode={topMode}
          onTopModeChange={setTopMode}
        />
        {/* Filter changes refetch with cached data still shown (isFetching),
            so surface a progress bar + dim the section as feedback. */}
        {periodQuery.isFetching && !periodQuery.isLoading && (
          <LinearProgress sx={{ borderRadius: 1 }} />
        )}
        {periodQuery.isLoading && (
          <SectionLoading label="Chargement des statistiques de la période…" />
        )}
        {periodQuery.isError && (
          <SectionError message="Échec du chargement des statistiques de la période." />
        )}
        {periodQuery.data && (
          <Box
            sx={{
              opacity: periodQuery.isFetching ? 0.5 : 1,
              transition: "opacity 0.2s",
              pointerEvents: periodQuery.isFetching ? "none" : "auto",
            }}
          >
            <Stack gap={3}>
              <AttractivitySection
                data={periodQuery.data}
                topMode={topMode}
                showEmailConfirmed={!isClient}
              />
              <EngagementSection data={periodQuery.data} />
              {!isClient && periodQuery.data.heatmap && (
                <VisitsHeatmap data={periodQuery.data.heatmap} />
              )}
            </Stack>
          </Box>
        )}
        {data && <PlayerProfileSection data={data} />}
        {!isClient && comparisonQuery.isLoading && (
          <SectionLoading label="Chargement des comparaisons…" />
        )}
        {!isClient && comparisonQuery.isError && (
          <SectionError message="Échec du chargement des comparaisons." />
        )}
        {comparisonQuery.data && (
          <ComparisonSection data={comparisonQuery.data} />
        )}
        {isClient && networkQuery.isLoading && (
          <SectionLoading label="Chargement des statistiques du réseau…" />
        )}
        {isClient && networkQuery.isError && (
          <SectionError message="Échec du chargement des statistiques du réseau." />
        )}
        {isClient && networkQuery.data && (
          <>
            <NetworkSection data={networkQuery.data} />
            <GeneralPlayerStatsSection data={networkQuery.data} />
            <NationalCollectionsTopSection data={networkQuery.data} />
          </>
        )}
      </Stack>
    </List>
  );
};

export const Dashboard = () => {
  const permissions = usePermissions<Role>({});
  const isClient = permissions.data === Role.CLIENT;
  const collectionsQuery = useGetDashboardCollections(isClient);

  if (permissions.isLoading || (isClient && collectionsQuery.isLoading))
    return <DashboardLoading />;

  if (
    permissions.isError ||
    permissions.data === undefined ||
    (isClient && (collectionsQuery.isError || !collectionsQuery.data))
  )
    return <DashboardError />;

  const initialFilters = isClient
    ? clientDefaultPeriodFilters(collectionsQuery.data ?? [])
    : defaultPeriodFilters();

  return (
    <DashboardContent
      key={`${getCurrentUserKey()}:${permissions.data}`}
      initialFilters={initialFilters}
      isClient={isClient}
    />
  );
};
